import { expandWildcards, type GrantedPermission } from '@/models/permission';

export interface Role {
  id: string;
  name: string;
  description: string;
  permissions: GrantedPermission[];
}

// Roles are a small, static set, so the API returns the full list without
// pagination.
export interface ListRolesResponse {
  roles: Role[];
}

// Human label per permission resource segment. An unmapped segment falls back to
// the raw string: the backend can add resources without breaking this view, and
// no authorization behavior is derived from these labels.
const RESOURCE_LABELS: Record<string, string> = {
  admin: 'Administration',
  bans: 'Bans',
  beacons: 'Beacons',
  bundles: 'Bundles',
  gifts: 'Gifts',
  inventory: 'Inventory',
  inventory_transactions: 'Inventory Transactions',
  items: 'Items',
  languages: 'Languages',
  players: 'Players',
  sandboxes: 'Sandboxes',
  seasonpass: 'Season Passes',
  store: 'Store',
  store_transactions: 'Store Transactions',
  translations: 'Translations',
};

// Wildcards are expanded first: a Role granting 'store:*' lists the five store
// actions it really confers, rather than a '*' the reader has to decode.
export const groupByResource = (permissions: GrantedPermission[]) => {
  const groups = new Map<string, string[]>();
  for (const { resource, action } of expandWildcards(permissions)) {
    const actions = groups.get(resource) ?? [];
    actions.push(action);
    groups.set(resource, actions);
  }

  return [...groups]
    .map(([resource, actions]) => ({
      resource,
      label: RESOURCE_LABELS[resource] ?? resource,
      actions: [...actions].sort(),
    }))
    .sort((a, b) => a.label.localeCompare(b.label));
};

// Counted after wildcard expansion, so 'store:*' reads as its five actions rather
// than as one permission.
export const permissionSummary = (permissions: GrantedPermission[]) => {
  const groups = groupByResource(permissions);
  return {
    groups,
    permissionCount: groups.reduce(
      (total, { actions }) => total + actions.length,
      0,
    ),
  };
};
